import type { DataImportSource } from "@/schema/data-import-source"
import { useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import { fetchDataImportSources } from "./api"

export function useFetchDataImportSources(options?: { refetchInterval?: number }) {
  const getContext = useApiContext()
  const bookId = useBookId()
  const pollInterval = options?.refetchInterval ?? 5000

  return useQuery<DataImportSource[]>({
    queryKey: ["data-import-sources", bookId],
    enabled: !!bookId,
    queryFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return fetchDataImportSources(ctx)
    },
    refetchInterval: (query) => {
      const sources = query.state.data ?? []
      const anyInFlight = sources.some((s) => {
        if (s.statistics.refreshing) return true
        if (
          s.status === "pending" ||
          s.status === "scraping" ||
          s.status === "parsing" ||
          s.status === "extracting" ||
          s.status === "awaiting_2fa" ||
          s.status === "awaiting_credentials"
        )
          return true
        if (s.data_imports.some((di) => di.status === "extracting" || di.status === "importing")) return true
        if (s.status === "ready" && s.data_imports.length === 0) return true
        return false
      })
      return anyInFlight ? pollInterval : false
    },
  })
}
